import { format, isAfter, isSameDay, parseISO, startOfDay, subDays } from 'date-fns';

/**
 * ProductDataProcessor
 *
 * Processa dados brutos de orders e calcula metricas agregadas por produto.
 * Todas as operacoes sao realizadas no cliente para permitir analise offline.
 */
class ProductDataProcessor {
    constructor(orders, dateRange, preset = null) {
        this.orders = orders || [];
        this.dateRange = dateRange || { from: null, to: null };
        this.preset = preset;

        // Determinar groupBy baseado no preset
        this.groupBy = this.determineGroupBy(preset);

        // Usar a data mais recente dos orders como referencia
        // Isso garante que "hoje" seja baseado nos dados reais, nao na data local
        this.today = this.getMostRecentOrderDate();

        // Calcular tamanho do periodo filtrado
        this.periodDays = this.calculatePeriodDays();
    }

    /**
     * Determina o tipo de agrupamento baseado nas colunas do preset
     * @param {Object} preset
     * @returns {string} 'item' ou 'inventory'
     */
    determineGroupBy(preset) {
        if (!preset || !preset.columns) {
            return 'item';
        }

        // Se o preset contem a coluna inventoryId, agrupar por inventory
        const hasInventoryColumn = preset.columns.some((col) => col.type === 'predefined' && col.key === 'inventoryId');

        return hasInventoryColumn ? 'inventory' : 'item';
    }

    /**
     * Calcula quantos dias tem o periodo filtrado
     * @returns {number}
     */
    calculatePeriodDays() {
        if (!this.dateRange.from || !this.dateRange.to) {
            return 0;
        }
        const from = parseISO(this.dateRange.from);
        const to = parseISO(this.dateRange.to);
        return Math.ceil((to - from) / (1000 * 60 * 60 * 24));
    }

    /**
     * Obtem a data mais recente dos orders
     * @returns {Date}
     */
    getMostRecentOrderDate() {
        let mostRecent = null;

        this.orders.forEach((order) => {
            if (order.date_created) {
                const orderDate = startOfDay(parseISO(order.date_created));
                if (!mostRecent || isAfter(orderDate, mostRecent) || isSameDay(orderDate, mostRecent)) {
                    mostRecent = orderDate;
                }
            }
        });

        return mostRecent || startOfDay(new Date());
    }

    /**
     * Processa orders e retorna array de produtos com metricas calculadas
     * @returns {Array<ProductMetrics>}
     */
    processProducts() {
        try {
            // 1. Extrair e agrupar items por produto
            const productMap = this.extractAndGroupItems();

            // 2. Calcular metricas para cada produto
            const products = [];

            productMap.forEach((productData, _key) => {
                const dailySales = this.calculateDailySales(productData.sales);
                const periodAggregations = this.calculatePeriodAggregations(dailySales);
                const analysisMetrics = this.calculateAnalysisMetrics(dailySales);
                const trend = this.calculateTrend(analysisMetrics.previousPeriod, analysisMetrics.twoPeriodsPast);

                products.push({
                    itemId: productData.itemId,
                    inventoryId: productData.inventoryId,
                    title: productData.title,
                    seller_sku: productData.seller_sku, // Incluir seller_sku
                    brand: productData.brand || null,
                    cost_price: productData.cost_price ?? null,
                    unit_price: productData.unit_price, // Incluir preco unitario

                    // Vendas diarias
                    today: this.getSalesForDay(dailySales, 0),
                    yesterday: this.getSalesForDay(dailySales, 1),
                    day2: this.getSalesForDay(dailySales, 2),
                    day3: this.getSalesForDay(dailySales, 3),

                    // Agregacoes de periodo
                    ...periodAggregations,

                    // Metricas de analise
                    ...analysisMetrics,

                    // Tendencia
                    ...trend,
                });
            });

            return products;
        } catch (_error) {
            throw new Error('Falha ao processar dados dos produtos');
        }
    }

    /**
     * Extrai items de todos os orders e agrupa por item_id ou variation_id
     * @returns {Map<string, {title: string, inventoryId: string, sales: Array}>}
     */
    extractAndGroupItems() {
        const productMap = new Map();

        this.orders.forEach((order) => {
            // Validar se order tem items
            if (!order.order_items || !Array.isArray(order.order_items)) {
                return;
            }

            // Validar se order tem data valida
            if (!order.date_created) {
                return;
            }

            const orderDate = parseISO(order.date_created);

            order.order_items.forEach((item) => {
                // Validar dados do item
                if (!item.item_id || !item.quantity) {
                    return;
                }

                // Determinar a chave de agrupamento
                let key;
                let inventoryId = null;

                if (this.groupBy === 'inventory') {
                    if (item.inventory_id_from_api) {
                        key = `${item.item_id}_${item.inventory_id_from_api}`;
                        inventoryId = item.inventory_id_from_api;
                    } else if (item.item_variation_id) {
                        // Fallback para variation_id
                        key = `${item.item_id}_${item.item_variation_id}`;
                        inventoryId = item.item_variation_id;
                    } else {
                        // Fallback: se nao tem variation_id, usar item_id
                        key = item.item_id;
                        inventoryId = 'N/A';
                    }
                } else {
                    // Agrupar por item_id (padrao)
                    key = item.item_id;
                    // Mesmo agrupando por item, capturar inventoryId se disponivel
                    inventoryId = item.inventory_id_from_api || item.item_variation_id || null;
                }

                if (!productMap.has(key)) {
                    productMap.set(key, {
                        itemId: item.item_id,
                        inventoryId: inventoryId,
                        title: item.item_title || item.item_seller_sku || item.sku || item.item_id || 'Produto',
                        seller_sku: item.item_seller_sku || item.sku || item.seller_sku || null, // Preservar seller_sku
                        brand: item.item_brand || item.brand || null,
                        cost_price:
                            item.item_cost_price !== null && item.item_cost_price !== undefined
                                ? Number(item.item_cost_price)
                                : item.cost_price !== null && item.cost_price !== undefined
                                  ? Number(item.cost_price)
                                : null,
                        unit_price: Number.parseFloat(item.unit_price) || 0, // Preservar preco unitario
                        sales: [],
                    });
                } else {
                    // Atualizar seller_sku se o atual for nulo e o novo nao for
                    const currentProduct = productMap.get(key);
                    const candidateSku = item.item_seller_sku || item.sku || item.seller_sku || null;
                    if (!currentProduct.seller_sku && candidateSku) {
                        currentProduct.seller_sku = candidateSku;
                    }
                    // Atualizar preco se o atual for zero e o novo nao for
                    if (!currentProduct.unit_price && item.unit_price) {
                        currentProduct.unit_price = Number.parseFloat(item.unit_price) || 0;
                    }
                    if (!currentProduct.brand && (item.item_brand || item.brand)) {
                        currentProduct.brand = item.item_brand || item.brand;
                    }
                    if (
                        (currentProduct.cost_price === null || currentProduct.cost_price === undefined) &&
                        ((item.item_cost_price !== null && item.item_cost_price !== undefined) ||
                            (item.cost_price !== null && item.cost_price !== undefined))
                    ) {
                        currentProduct.cost_price = Number(
                            item.item_cost_price !== null && item.item_cost_price !== undefined
                                ? item.item_cost_price
                                : item.cost_price,
                        );
                    }
                }

                productMap.get(key).sales.push({
                    date: orderDate,
                    quantity: Number.parseInt(item.quantity, 10) || 0,
                });
            });
        });

        return productMap;
    }

    /**
     * Calcula vendas diarias para um produto
     * @param {Array<{date: Date, quantity: number}>} sales
     * @returns {Map<string, number>}
     */
    calculateDailySales(sales) {
        const dailyMap = new Map();

        sales.forEach(({ date, quantity }) => {
            const dateKey = format(date, 'yyyy-MM-dd');
            dailyMap.set(dateKey, (dailyMap.get(dateKey) || 0) + quantity);
        });

        return dailyMap;
    }

    /**
     * Obtem vendas de um dia especifico (relativo a hoje)
     * @param {Map<string, number>} dailySales
     * @param {number} daysAgo - Numero de dias atras (0 = hoje)
     * @returns {number}
     */
    getSalesForDay(dailySales, daysAgo) {
        const targetDate = subDays(this.today, daysAgo);
        const dateKey = format(targetDate, 'yyyy-MM-dd');
        const sales = dailySales.get(dateKey) || 0;

        return sales;
    }

    /**
     * Calcula agregacoes de periodo (ultimos X dias)
     * @param {Map<string, number>} dailySales
     * @returns {Object}
     */
    calculatePeriodAggregations(dailySales) {
        const periods = [3, 5, 7, 15, 30, 60, 90, 180, 365];
        const aggregations = {};

        periods.forEach((days) => {
            const startDate = subDays(this.today, days - 1); // -1 porque inclui hoje
            let total = 0;

            dailySales.forEach((quantity, dateStr) => {
                const date = parseISO(dateStr);
                if (isAfter(date, startDate) || isSameDay(date, startDate)) {
                    if (!isAfter(date, this.today)) {
                        // Nao incluir datas futuras
                        total += quantity;
                    }
                }
            });

            aggregations[`last${days}d`] = total;
        });

        return aggregations;
    }

    /**
     * Calcula metricas de analise (melhor dia, media, meses)
     * @param {Map<string, number>} dailySales
     * @returns {Object}
     */
    calculateAnalysisMetrics(dailySales) {
        // Melhor dia
        let bestDay = { date: null, quantity: 0 };
        dailySales.forEach((quantity, dateStr) => {
            if (quantity > bestDay.quantity) {
                bestDay = { date: dateStr, quantity };
            }
        });

        // Periodo atual (tamanho do filtro)
        const currentPeriod = this.calculatePeriodTotal(dailySales, 0, this.periodDays - 1);

        // Periodo passado (mesmo tamanho, mas anterior)
        const previousPeriod = this.calculatePeriodTotal(dailySales, this.periodDays, this.periodDays * 2 - 1);

        // Periodo retrasado (mesmo tamanho, mas 2x anterior)
        const twoPeriodsPast = this.calculatePeriodTotal(dailySales, this.periodDays * 2, this.periodDays * 3 - 1);

        // Media CORRIGIDA: total do periodo filtrado / dias no periodo filtrado
        const average = this.periodDays > 0 ? Number.parseFloat((currentPeriod / this.periodDays).toFixed(1)) : 0;

        return {
            bestDay,
            average,
            currentPeriod,
            previousPeriod,
            twoPeriodsPast,
        };
    }

    /**
     * Calcula total de vendas em um periodo especifico
     * @param {Map<string, number>} dailySales
     * @param {number} startDaysAgo - Inicio do periodo (dias atras)
     * @param {number} endDaysAgo - Fim do periodo (dias atras)
     * @returns {number}
     */
    calculatePeriodTotal(dailySales, startDaysAgo, endDaysAgo) {
        const startDate = subDays(this.today, endDaysAgo);
        const endDate = subDays(this.today, startDaysAgo);
        let total = 0;

        dailySales.forEach((quantity, dateStr) => {
            const date = parseISO(dateStr);
            if ((isAfter(date, startDate) || isSameDay(date, startDate)) && !isAfter(date, endDate)) {
                total += quantity;
            }
        });

        return total;
    }

    /**
     * Calcula tendencia comparando dois periodos
     * @param {number} lastMonth - Vendas do ultimo mes
     * @param {number} previousMonth - Vendas do mes anterior
     * @returns {Object}
     */
    calculateTrend(lastMonth, previousMonth) {
        if (previousMonth === 0) {
            return {
                trend: 'stable',
                trendPercentage: 0,
            };
        }

        const percentage = ((lastMonth - previousMonth) / previousMonth) * 100;

        let trend = 'stable';
        if (percentage > 5) {
            trend = 'up';
        } else if (percentage < -5) {
            trend = 'down';
        }

        return {
            trend,
            trendPercentage: Number.parseFloat(percentage.toFixed(1)),
        };
    }
}

export default ProductDataProcessor;
